@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.setup

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.ScreenManager
import org.example.project.enginekartei.KarteiApi
import org.example.project.registrierung.NetzwerkLoginStyles
import org.example.project.registrierung.RoleIntroSelection
import org.example.project.registrierung.DesktopRegistrierungApi
import org.example.project.screens_cus.standortStructureFields
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.PasswordPolicy
import org.example.project.service.RollenService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.PasswordRequirementHint
import org.example.project.settings.UISettings
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.example.project.utils.All.AllNames.names
import org.example.project.utils.DashboardVerlaufHintergrund
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private const val SETUP_STEP_COUNT = 6

private fun setupLabel(fieldId: String, fallback: String = fieldId): String = names[fieldId] ?: fallback

private fun setupUserShortcut(vorname: String, nachname: String): String = buildString {
    if (vorname.isNotBlank()) append(vorname.take(1).uppercase())
    if (nachname.isNotBlank()) append(nachname.take(1).uppercase())
}.ifBlank { "AD" }

private fun normalizedSetupValue(value: String): String = value.trim().lowercase()

private fun isRecognizedNetworkCustomer(entry: All): Boolean =
    entry.istnetzwerk || (entry.istkunde && entry.snapNetzwerkfunktionen)

private fun sameRegisterCompany(registerFirma: All?, user: All, currentFirma: String): Boolean {
    val registerFirmenId = registerFirma?.firmenId.orEmpty().trim()
    val registerId = registerFirma?.id.orEmpty().trim()
    val registerName = normalizedSetupValue(registerFirma?.firma.orEmpty())
    val currentName = normalizedSetupValue(currentFirma)
    val userFirmenId = user.firmenId.trim()
    val userFirma = normalizedSetupValue(user.firma.ifBlank { user.kunde })

    return when {
        registerFirmenId.isNotBlank() && userFirmenId == registerFirmenId -> true
        registerId.isNotBlank() && userFirmenId == registerId -> true
        registerName.isNotBlank() && userFirma == registerName -> true
        currentName.isNotBlank() && userFirma == currentName -> true
        else -> false
    }
}

@Composable
fun CUSlumaticasetup(onComplete: () -> Unit) {
    Style(cssRules = NetzwerkLoginStyles.cssRules)
    DashboardVerlaufHintergrund()

    val scope = rememberCoroutineScope()
    var step by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var setupUser by remember { mutableStateOf<All?>(null) }

    var vorname by remember { mutableStateOf("") }
    var nachname by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var passwort by remember { mutableStateOf("") }
    var passwortWdh by remember { mutableStateOf("") }

    val setupRoleId = "1"
    var setupRoleName by remember { mutableStateOf("") }
    var selectedRolleId by remember { mutableStateOf(setupRoleId) }
    var registerFirma by remember { mutableStateOf<All?>(null) }
    var setupStandort by remember { mutableStateOf<All?>(null) }
    var existingNetworkCustomer by remember { mutableStateOf(false) }
    var manualUserCreation by remember { mutableStateOf(false) }
    var position by remember { mutableStateOf("") }
    var selectedPositionId by remember { mutableStateOf("PRODUZENT") }

    var firma by remember { mutableStateOf("") }
    var geschaeftsfuehrer by remember { mutableStateOf("") }
    var webseite by remember { mutableStateOf("") }
    var gegruendetAm by remember { mutableStateOf("") }
    var strasse by remember { mutableStateOf("") }
    var hausnummer by remember { mutableStateOf("") }
    var plz by remember { mutableStateOf("") }
    var ort by remember { mutableStateOf("") }
    var halleVon by remember { mutableStateOf("") }
    var halleBis by remember { mutableStateOf("") }
    var regalVon by remember { mutableStateOf("") }
    var regalBis by remember { mutableStateOf("") }
    var fachVon by remember { mutableStateOf("") }
    var fachBis by remember { mutableStateOf("") }
    var ebeneVon by remember { mutableStateOf("") }
    var ebeneBis by remember { mutableStateOf("") }
    var toreVon by remember { mutableStateOf("") }
    var toreBis by remember { mutableStateOf("") }
    var parkplatzVon by remember { mutableStateOf("") }
    var parkplatzBis by remember { mutableStateOf("") }


    fun applyCompanyData(data: All) {
        firma = data.firma.ifBlank { firma }
        geschaeftsfuehrer = data.geschaeftsfuehrer.ifBlank { geschaeftsfuehrer }
        webseite = data.webseite.ifBlank { webseite }
        gegruendetAm = data.gegruendetAm.ifBlank { gegruendetAm }
        strasse = data.strasse.ifBlank { strasse }
        hausnummer = data.hausnummer.ifBlank { hausnummer }
        plz = data.plz.ifBlank { plz }
        ort = data.ort.ifBlank { ort }
        position = data.position.ifBlank { position }
    }

    fun applyInfrastructureData(data: All) {
        halleVon = data.halleVon.ifBlank { halleVon }
        halleBis = data.halleBis.ifBlank { halleBis }
        regalVon = data.regalVon.ifBlank { regalVon }
        regalBis = data.regalBis.ifBlank { regalBis }
        fachVon = data.fachVon.ifBlank { fachVon }
        fachBis = data.fachBis.ifBlank { fachBis }
        ebeneVon = data.ebeneVon.ifBlank { ebeneVon }
        ebeneBis = data.ebeneBis.ifBlank { ebeneBis }
        toreVon = data.toreVon.ifBlank { toreVon }
        toreBis = data.toreBis.ifBlank { toreBis }
        parkplatzVon = data.parkplatzVon.ifBlank { parkplatzVon }
        parkplatzBis = data.parkplatzBis.ifBlank { parkplatzBis }
    }

    fun infrastructureValue(id: String): String = when (id) {
        "halleVon" -> halleVon
        "halleBis" -> halleBis
        "regalVon" -> regalVon
        "regalBis" -> regalBis
        "fachVon" -> fachVon
        "fachBis" -> fachBis
        "ebeneVon" -> ebeneVon
        "ebeneBis" -> ebeneBis
        "toreVon" -> toreVon
        "toreBis" -> toreBis
        "parkplatzVon" -> parkplatzVon
        "parkplatzBis" -> parkplatzBis
        else -> ""
    }

    fun updateInfrastructureValue(id: String, value: String) {
        when (id) {
            "halleVon" -> halleVon = value
            "halleBis" -> halleBis = value
            "regalVon" -> regalVon = value
            "regalBis" -> regalBis = value
            "fachVon" -> fachVon = value
            "fachBis" -> fachBis = value
            "ebeneVon" -> ebeneVon = value
            "ebeneBis" -> ebeneBis = value
            "toreVon" -> toreVon = value
            "toreBis" -> toreBis = value
            "parkplatzVon" -> parkplatzVon = value
            "parkplatzBis" -> parkplatzBis = value
        }
    }

    fun setupGeschaeftsfuehrer(): String =
        geschaeftsfuehrer.ifBlank { listOf(vorname, nachname).filter { it.isNotBlank() }.joinToString(" ") }

    fun infrastructureLabel(id: String): String =
        names[id] ?: standortStructureFields.firstOrNull { it.id == id }?.label ?: id

    fun registerKey(data: All?): String =
        data?.firmenId?.ifBlank { data.id }
            ?: GlobalManager.currentFirmenId.trim().ifBlank { firma.ifBlank { "firma" } }

    @Composable
    fun InfrastructureInput(fieldId: String) {
        Div({
            style {
                width(100.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
            }
        }) {
            SetupInput(InputType.Text, infrastructureValue(fieldId), infrastructureLabel(fieldId)) {
                updateInfrastructureValue(fieldId, it)
            }
        }
    }

    LaunchedEffect(Unit) {
        val firmenId = SetupNodeApi.loadNodeFirmenId()
        val firma = if (firmenId.isBlank()) null else SetupApi.loadCusCompany()
        if (firma != null) {
            registerFirma = firma
            applyCompanyData(firma)
            applyInfrastructureData(firma)
            if (firma.istnetzwerk) {
                existingNetworkCustomer = true
                manualUserCreation = false
                step = 1
            }
        }
        val standort = SetupApi.loadCusStandorte().sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }.firstOrNull()
        if (standort != null) {
            setupStandort = standort
            applyInfrastructureData(standort)
        }
    }

    fun savePositionAndContinue(nextPosition: String) {
        if (loading) return
        loading = true
        error = null
        position = nextPosition
        scope.launch {
            try {
                val current = registerFirma
                val key = current?.firmenId?.ifBlank { current.id }
                    ?: GlobalManager.currentFirmenId.trim().ifBlank { firma.ifBlank { "firma" } }
                val updated = (current ?: All(id = key, firmenId = key, firma = firma)).copy(
                    id = key,
                    firmenId = key,
                    position = nextPosition,
                    firma = current?.firma?.ifBlank { firma } ?: firma,
                    geschaeftsfuehrer = current?.geschaeftsfuehrer?.ifBlank { setupGeschaeftsfuehrer() } ?: setupGeschaeftsfuehrer(),
                    webseite = current?.webseite.orEmpty(),
                    gegruendetAm = current?.gegruendetAm.orEmpty(),
                    strasse = current?.strasse?.ifBlank { strasse } ?: strasse,
                    hausnummer = current?.hausnummer?.ifBlank { hausnummer } ?: hausnummer,
                    plz = current?.plz?.ifBlank { plz } ?: plz,
                    ort = current?.ort?.ifBlank { ort } ?: ort,
                    istnetzwerk = current?.istnetzwerk ?: true
                )
                SetupApi.saveCusCompany(updated)
                registerFirma = updated
                applyCompanyData(updated)
                step = 1
            } catch (_: Throwable) {
                error = "Position konnte nicht gespeichert werden."
            } finally {
                loading = false
            }
        }
    }

    fun saveUserAndContinue() {
        val passwordError = PasswordPolicy.error(passwort)
        if (vorname.isBlank() || nachname.isBlank() || email.isBlank() || passwordError != null || passwort != passwortWdh) {
            error = passwordError ?: "Bitte User vollständig ausfüllen."
            return
        }
        if (loading) return
        loading = true
        error = null
        scope.launch {
            try {
                val users = SetupApi.loadCusUsers().filter { it.firmenId == registerKey(registerFirma) }
                val id = setupUser?.id?.ifBlank { null }
                    ?: ((users.mapNotNull { it.id.toIntOrNull() }.maxOrNull() ?: 0) + 1).toString()
                val name = listOf(vorname, nachname).filter { it.isNotBlank() }.joinToString(" ")
                val user = All(
                    id = id,
                    vorname = vorname,
                    nachname = nachname,
                    name = name,
                    email = email.trim().lowercase(),
                    personalnummer = setupUser?.personalnummer.orEmpty(),
                    kuerzel = setupUserShortcut(vorname, nachname),
                    rolle = setupRoleId,
                    position = position,
                    firmenId = registerKey(registerFirma),
                    firma = firma,
                    kunde = firma,
                    zugangDesktop = true,
                    zugangMobil = true,
                    gesperrt = false,
                    datenschutzAkzeptiert = false,
                )
                SetupApi.saveCusUser(user)
                val passwordUser = SetupApi.changeCusUserPassword(id, passwort)
                    ?.takeIf { it.passwortVerifiziert }
                    ?: error("Passwort konnte nicht gespeichert werden.")
                setupUser = SetupApi.loadCusUser(id) ?: passwordUser
                step = 2
            } catch (_: Throwable) {
                error = "User konnte nicht gespeichert werden."
            } finally {
                loading = false
            }
        }
    }

    suspend fun saveExistingNetworkUser(registerUser: All) {
        val users = SetupApi.loadCusUsers().filter { it.firmenId == registerKey(registerFirma) }
        val existingLocal = users.firstOrNull { it.email.equals(registerUser.email, ignoreCase = true) }
        val registerId = registerUser.id.ifBlank { null }
        val idInUseByOther = registerId != null && users.any {
            it.id == registerId && !it.email.equals(registerUser.email, ignoreCase = true)
        }
        val id = existingLocal?.id
            ?: registerId?.takeUnless { idInUseByOther }
            ?: ((users.mapNotNull { it.id.toIntOrNull() }.maxOrNull() ?: 0) + 1).toString()
        val firstName = registerUser.vorname
        val lastName = registerUser.nachname
        val name = registerUser.name.ifBlank {
            listOf(firstName, lastName).filter { it.isNotBlank() }.joinToString(" ")
        }
        val user = All(
            id = id,
            vorname = firstName,
            nachname = lastName,
            name = name,
            email = registerUser.email.trim().lowercase(),
            personalnummer = registerUser.personalnummer,
            kuerzel = registerUser.kuerzel.ifBlank { setupUserShortcut(firstName, lastName) },
            rolle = registerUser.rolle.ifBlank { setupRoleId },
            position = registerUser.position.ifBlank { position },
            firmenId = registerUser.firmenId.ifBlank { registerKey(registerFirma) },
            firma = firma.ifBlank { registerUser.firma },
            kunde = firma.ifBlank { registerUser.kunde },
            zugangDesktop = true,
            zugangMobil = true,
            aktiv = registerUser.aktiv,
            gesperrt = registerUser.gesperrt ?: false,
            datenschutzAkzeptiert = registerUser.datenschutzAkzeptiert,
            passwortVerifiziert = registerUser.passwortVerifiziert,
            terminkalenderOnline = registerUser.terminkalenderOnline,
            terminkalenderIntern = registerUser.terminkalenderIntern,
            profilOnline = registerUser.profilOnline,
            profilIntern = registerUser.profilIntern
        )
        SetupApi.saveCusUser(user)
        val passwordUser = if (passwort.isNotBlank()) {
            SetupApi.changeCusUserPassword(id, passwort)
                ?.takeIf { it.passwortVerifiziert }
                ?: error("Passwort konnte nicht gespeichert werden.")
        } else {
            user
        }
        setupUser = SetupApi.loadCusUser(id) ?: passwordUser
        vorname = user.vorname
        nachname = user.nachname
        email = user.email
        passwort = ""
        passwortWdh = ""
        step = 2
    }

    fun confirmExistingNetworkAccount() {
        val mail = email.trim().lowercase()
        if (mail.isBlank() || passwort.isBlank()) {
            error = "Bitte E-Mail und Passwort eingeben."
            return
        }
        if (loading) return
        loading = true
        error = null
        scope.launch {
            try {
                val loginResult = DesktopRegistrierungApi.loginDetailed(mail, passwort)
                val user = loginResult.user?.takeIf { sameRegisterCompany(registerFirma, it, firma) }
                if (user == null) {
                    error = when (loginResult.error) {
                        "email_invalid" -> "E-Mail-Adresse ist ungültig."
                        "email_unknown" -> "E-Mail ist nicht bekannt."
                        "password_invalid" -> "Passwort falsch."
                        "rate_limited" -> "Zu viele Anmeldeversuche. Bitte 60 Sekunden warten."
                        "user_blocked" -> "Der Benutzerzugang ist gesperrt."
                        "company_blocked" -> "Der Unternehmenszugang ist gesperrt."
                        "company_not_active" -> "Der Unternehmenszugang ist noch nicht aktiv."
                        "network_error" -> "Server nicht erreichbar. Bitte Verbindung prüfen."
                        "" -> "Der Account gehört nicht zu dieser Firma."
                        else -> "Account konnte technisch nicht geprüft werden."
                    }
                    return@launch
                }
                LoginService.captureSession(user)
                saveExistingNetworkUser(user)
            } catch (_: Throwable) {
                error = "Account konnte nicht geprüft werden."
            } finally {
                loading = false
            }
        }
    }

    fun assignRoleAndContinue() {
        val user = setupUser ?: run {
            error = "Bitte zuerst User anlegen."
            step = 1
            return
        }
        val roleName = setupRoleName.trim()
        if (roleName.isBlank()) {
            error = "Bitte Rolle eintragen."
            return
        }
        loading = true
        error = null
        scope.launch {
            try {
                val role = All(
                    id = setupRoleId,
                    name = roleName,
                    bezeichnung = roleName,
                    screenBerechtigungen = ScreenManager.allIndexKeys().associateWith { RollenService.ALLES }
                )
                SetupApi.saveCusRole(user.id, registerKey(registerFirma), role)
                val updatedUser = user.copy(
                    rolle = setupRoleId,
                    zugangDesktop = true,
                    zugangMobil = true
                )
                SetupApi.saveCusUser(updatedUser)
                setupUser = SetupApi.loadCusUser(user.id) ?: updatedUser
                selectedRolleId = setupRoleId
                step = 3
            } catch (_: Throwable) {
                error = "Rolle konnte nicht zugeordnet werden."
            } finally {
                loading = false
            }
        }
    }

    fun saveCompanyAndContinue() {
        if (firma.isBlank()) {
            error = "Bitte Firma eintragen."
            return
        }
        loading = true
        error = null
        scope.launch {
            try {
                val current = registerFirma
                val key = current?.firmenId?.ifBlank { current.id }
                    ?: GlobalManager.currentFirmenId.trim().ifBlank { firma }
                val updated = if (key.isNotBlank()) {
                    (current ?: All(id = key, firmenId = key)).copy(
                        id = key,
                        firmenId = key,
                        position = position,
                        firma = firma,
                        geschaeftsfuehrer = setupGeschaeftsfuehrer(),
                        webseite = webseite,
                        gegruendetAm = gegruendetAm,
                        strasse = strasse,
                        hausnummer = hausnummer,
                        plz = plz,
                        ort = ort,
                        istnetzwerk = current?.istnetzwerk ?: true,
                        istkunde = current?.istkunde ?: false
                    )
                } else {
                    current ?: All()
                }
                if (key.isNotBlank()) {
                    registerFirma = SetupApi.saveCusCompany(updated) ?: updated
                }
                step = 4
            } catch (_: Throwable) {
                error = "Firmendaten konnten nicht gespeichert werden."
            } finally {
                loading = false
            }
        }
    }

    fun finishSetup() {
        loading = true
        error = null
        scope.launch {
            try {
                val standortId = setupStandort?.id?.takeIf { it.isNotBlank() } ?: "1"
                val currentRegister = registerFirma
                val standort = (setupStandort ?: All(id = standortId)).copy(
                    id = standortId,
                    firma = "",
                    geschaeftsfuehrer = "",
                    firmenId = registerKey(currentRegister),
                    istnetzwerk = currentRegister?.istnetzwerk ?: false,
                    istkunde = currentRegister?.istkunde ?: false,
                    standort = setupStandort?.standort?.ifBlank { firma } ?: firma,
                    strasse = strasse,
                    hausnummer = hausnummer,
                    plz = plz,
                    ort = ort,
                    halleVon = halleVon,
                    halleBis = halleBis,
                    regalVon = regalVon,
                    regalBis = regalBis,
                    fachVon = fachVon,
                    fachBis = fachBis,
                    ebeneVon = ebeneVon,
                    ebeneBis = ebeneBis,
                    toreVon = toreVon,
                    toreBis = toreBis,
                    parkplatzVon = parkplatzVon,
                    parkplatzBis = parkplatzBis
                )
                SetupApi.saveCusStandort(registerKey(registerFirma), standort)
                SetupApi.saveCusNodeEingerichtet(true)
                LoginService.clearLocalLogin()
                GlobalManager.triggerSessionConfigReload()
                onComplete()
            } catch (_: Throwable) {
                error = "Setup konnte nicht abgeschlossen werden."
            } finally {
                loading = false
            }
        }
    }

    Header({ classes(NetzwerkLoginStyles.topBar) }) {
        Div({ classes(NetzwerkLoginStyles.headerContent) }) {
            Div({ classes(NetzwerkLoginStyles.centerBlock) }) {
                Img(src = LogoSettings.desktopRegistrationHeaderLogo, attrs = {
                    classes(NetzwerkLoginStyles.centerLogo)
                    style { height(LogoSettings.desktopRegistrationHeaderLogoHeightPx.px) }
                })
            }
        }
    }

    Main({ classes(NetzwerkLoginStyles.mainContent) }) {
        Div({
            classes(NetzwerkLoginStyles.gifWrapper)
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                width(100.percent)
                gap(20.px)
            }
        }) {
            val frameWidth = 850.px
            Div({
                style {
                    width(frameWidth)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Stretch)
                    gap(8.px)
                }
            }) {
                Div({
                    classes(NetzwerkLoginStyles.registerFrame)
                    style {
                        position(Position.Relative)
                        width(100.percent)
                        height(550.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        justifyContent(JustifyContent.SpaceBetween)
                        padding(34.px)
                    }
                }) {
                    val firstStep = if (existingNetworkCustomer) 1 else 0
                    if (step > firstStep) {
                        Img(src = "images/back.svg", attrs = {
                            classes(NetzwerkLoginStyles.navIcon)
                            style { property("left", "42px") }
                            onClick { step-- }
                        })
                    }

                    when (step) {
                        0 -> {
                            RoleIntroSelection(
                                selectedRoleId = selectedPositionId,
                                onSelect = { selectedPositionId = it },
                                onConfirm = { savePositionAndContinue(selectedPositionId) },
                                title = "Willkommen im lumatica OS Setup",
                                subtitle = "Welche Position haben Sie in der Logistik?"
                            )
                            SetupError(error)
                        }
                        1 -> {
                            SetupPanelBody {
                                if (existingNetworkCustomer && !manualUserCreation) {
                                    H2 { Text("Hallo ${firma.ifBlank { "Firma" }}") }
                                    P { Text("Wir freuen uns Sie von nun an als Kunde begrüßen zu dürfen.") }
                                    P { Text("Bitte melden Sie sich mit Ihrem bestehenden Account an oder erstellen Sie einen neuen, falls Sie keinen haben.") }
                                    Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                        SetupInput(InputType.Email, email, setupLabel("email", "E-Mail")) { email = it }
                                        SetupInput(InputType.Password, passwort, "Passwort") { passwort = it }
                                        SetupError(error)
                                    }
                                } else {
                                    H2 { Text("User festlegen") }
                                    P { Text("Bitte legen Sie Ihren persönlichen Zugang zum System fest.") }
                                    Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                        Div({ classes(NetzwerkLoginStyles.row) }) {
                                            SetupInput(InputType.Text, vorname, setupLabel("vorname", "Vorname")) { vorname = it }
                                            SetupInput(InputType.Text, nachname, setupLabel("nachname", "Nachname")) { nachname = it }
                                        }
                                        SetupInput(InputType.Email, email, setupLabel("email", "E-Mail")) { email = it }
                                        SetupInput(InputType.Password, passwort, "Passwort") { passwort = it }
                                        PasswordRequirementHint(passwort)
                                        SetupInput(InputType.Password, passwortWdh, "Passwort wiederholen") { passwortWdh = it }
                                        SetupError(error)
                                    }
                                }
                            }
                            SetupPanelActions {
                                if (existingNetworkCustomer && !manualUserCreation) {
                                    LumaticaButton(if (loading) "..." else "Bestätigen", ::confirmExistingNetworkAccount, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 44)
                                    LumaticaButton("Ich habe keinen Account", {
                                        manualUserCreation = true
                                        passwort = ""
                                        passwortWdh = ""
                                        error = null
                                    }, enabled = !loading, heightPx = 44)
                                } else {
                                    LumaticaButton(if (loading) "..." else "Weiter", ::saveUserAndContinue, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 44)
                                }
                            }
                        }
                        2 -> {
                            SetupPanelBody {
                                H2 { Text("Bitte legen Sie jetzt Ihre Rolle für Ihren Zugang an.") }
                                P { Text("Diese Rolle wird Sie ab sofort exklusive Zugänge im System bieten.") }

                            }
                            Div({
                                style {
                                    position(Position.Absolute)
                                    property("top", "50%")
                                    property("left", "50%")
                                    property("transform", "translate(-50%, -50%)")
                                    width(100.percent)
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    alignItems(AlignItems.Center)
                                    gap(22.px)
                                }
                            }) {
                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    Div({
                                        style {
                                            width(100.percent)
                                            property("max-width", "360px")
                                            property("margin-left", "auto")
                                            property("margin-right", "auto")
                                        }
                                    }) {
                                        SetupInput(InputType.Text, setupRoleName, "") { setupRoleName = it }
                                    }
                                    SetupError(error)
                                }
                            }
                            SetupPanelActions {
                                LumaticaButton(if (loading) "..." else "Weiter", ::assignRoleAndContinue, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 42)
                            }
                        }
                        3 -> {
                            SetupPanelBody {
                                H2 { Text("Folgender Standort wurde im System gefunden:") }
                                P { Text("Weitere Standorte können später problemlos hinzugefügt werden ") }
                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    SetupInput(InputType.Text, firma, setupLabel("firmenname", "Firma")) { firma = it }
                                    SetupInput(InputType.Text, geschaeftsfuehrer, setupLabel("geschaeftsfuehrer", "Geschäftsführer")) { geschaeftsfuehrer = it }
                                    Div({ classes(NetzwerkLoginStyles.row) }) {
                                        SetupInput(InputType.Text, webseite, setupLabel("webseite", "Webseite")) { webseite = it }
                                        SetupInput(InputType.Text, gegruendetAm, setupLabel("gegruendetAm", "Gegründet am")) { gegruendetAm = it }
                                    }
                                    Div({ classes(NetzwerkLoginStyles.row) }) {
                                        SetupInput(InputType.Text, strasse, "Straße") { strasse = it }
                                        SetupInput(InputType.Text, hausnummer, setupLabel("hausnummer", "Hausnummer")) { hausnummer = it }
                                    }
                                    Div({ classes(NetzwerkLoginStyles.row) }) {
                                        SetupInput(InputType.Text, plz, setupLabel("plz", "PLZ")) { plz = it }
                                        SetupInput(InputType.Text, ort, setupLabel("ort", "Ort")) { ort = it }
                                    }
                                    SetupError(error)
                                }
                            }
                            SetupPanelActions {
                                LumaticaButton(if (loading) "..." else "Weiter", ::saveCompanyAndContinue, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 44)
                            }
                        }
                        4 -> {
                            SetupPanelBody {
                                H2 { Text("Legen Sie jetzt Ihre Infrastruktur fest.") }
                                P { Text("Wie viele Hallen, Tore und Parkplätze haben Sie an diesem Standort?") }
                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    listOf(
                                        listOf("halleVon", "halleBis"),
                                        listOf("toreVon", "toreBis"),
                                        listOf("parkplatzVon", "parkplatzBis")
                                    ).forEach { row ->
                                        Div({ classes(NetzwerkLoginStyles.row) }) {
                                            row.forEach { fieldId ->
                                                InfrastructureInput(fieldId)
                                            }
                                        }
                                    }
                                }
                                SetupError(error)
                            }
                            SetupPanelActions {
                                LumaticaButton(if (loading) "..." else "Weiter", { step = 5 }, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 42)
                            }
                        }
                        5 -> {
                            SetupPanelBody {
                                H2 { Text("Legen Sie jetzt Ihre Infrastruktur fest.") }
                                P { Text("Wie viele Regale haben Sie an diesem Standort?") }
                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    listOf(
                                        listOf("regalVon", "regalBis"),
                                        listOf("fachVon", "fachBis"),
                                        listOf("ebeneVon", "ebeneBis")
                                    ).forEach { row ->
                                        Div({ classes(NetzwerkLoginStyles.row) }) {
                                            row.forEach { fieldId ->
                                                InfrastructureInput(fieldId)
                                            }
                                        }
                                    }
                                }
                                SetupError(error)
                            }
                            SetupPanelActions {
                                LumaticaButton(if (loading) "..." else "Setup abschließen", ::finishSetup, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 42)
                            }
                        }
                    }                }
                SetupProgressTabs(
                    currentStep = step,
                    firstStep = if (existingNetworkCustomer) 1 else 0,
                    onStepClick = { index -> if (index <= step) step = index }
                )
            }
        }
    }
}
@Composable
private fun SetupProgressTabs(currentStep: Int, firstStep: Int = 0, onStepClick: (Int) -> Unit) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            gap(6.px)
            height(10.px)
            alignItems(AlignItems.Center)
        }
    }) {
        repeat(SETUP_STEP_COUNT) { index ->
            Div({
                style {
                    property("flex", "1")
                    height(3.px)
                    backgroundColor(if (index == currentStep) UISettings.ACCENT else DashboardTheme.BORDER_LIGHT)
                }
                if (index in firstStep..currentStep) {
                    onClick { onStepClick(index) }
                }
            }) {}
        }
    }
}
@Composable
private fun SetupPanelBody(content: @Composable () -> Unit) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        content()
    }
}

@Composable
private fun SetupPanelActions(content: @Composable () -> Unit) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        content()
    }
}
@Composable
private fun SetupInput(type: InputType<String>, value: String, label: String, onInput: (String) -> Unit) {
    Input(type, attrs = {
        classes(NetzwerkLoginStyles.regInput)
        placeholder(label)
        value(value)
        onInput { onInput(it.value) }
    })
}

@Composable
private fun SetupError(text: String?) {
    if (!text.isNullOrBlank()) {
        P({ classes(NetzwerkLoginStyles.errorParagraph) }) { Text(text) }
    }
}
